/**
 * In-app upgrade / paywall. Shown when a free user clicks "Upgrade" from dashboard/sidebar/settings.
 * Focus: convert free users to Owner-Operator (recommended) or Fleet. Only paid plans are shown.
 * Billing: Stripe checkout via create-checkout; plan config in src/config/plans.js.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import posthog from 'posthog-js';
import API_URL from '../../config';
import { PAYWALL_PLANS, OWNER_OPERATOR_VEHICLE_LIMIT } from '../../config/plans';
import PricingCard from './PricingCard';
import PromoCodeInput from './PromoCodeInput';
import '../../App.css';

const VALUE_POINTS = [
  'Stay ahead of expirations',
  'Track compliance trends over time',
  'Turn records into real operational clarity',
];

const PricingPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vehicleCount, setVehicleCount] = useState(null);
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
  }, []);

  // Optional: fetch vehicle count to suggest Fleet when user already has more than Owner-Operator limit.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(`${API_URL}/api/vehicles`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setVehicleCount(Array.isArray(res.data) ? res.data.length : 0))
      .catch(() => setVehicleCount(null));
  }, []);

  const currentTier = (user?.subscription?.tier || user?.subscriptionTier || 'free').toLowerCase();
  const currentPlan = currentTier === 'pro' || currentTier === 'fleet' ? 'fleet' : currentTier;
  const currentStatus = String(user?.subscription?.status || '').toLowerCase();
  const hasActivePaidPlan = Boolean(
    user &&
      (currentPlan === 'fleet' || currentPlan === 'owner-operator') &&
      (currentStatus
        ? currentStatus === 'active' ||
          currentStatus === 'trialing' ||
          currentStatus === 'past_due'
        : true),
  );
  const suggestFleet = vehicleCount != null && vehicleCount > OWNER_OPERATOR_VEHICLE_LIMIT;

  const handleSelectPlan = async (tier, quantity) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const body = { tier };
      if (tier === 'fleet') {
        body.quantity = quantity;
        body.returnTo = 'pricing';
      }
      if (promo) {
        body.promoCode = promo.code;
      }
      const { data } = await axios.post(
        `${API_URL}/api/subscriptions/create-checkout`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.checkoutUrl) {
        posthog.capture('upgrade_initiated', {
          plan: tier,
          fromPlan: currentTier,
          hasPromo: Boolean(promo),
        });
        window.location.href = data.checkoutUrl;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      const raw = err.response?.data?.message;
      const message = Array.isArray(raw) ? raw[0] : raw;
      alert(message || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pricing-page paywall-page">
      <header className="paywall-header">
        <h1 className="paywall-title">Upgrade your compliance command center</h1>
        <p className="paywall-subtitle">
          Unlock deeper visibility, smarter alerts, and the tools to stay compliance-verified.
        </p>
      </header>

      <div className="paywall-value-strip">
        {VALUE_POINTS.map((point, idx) => (
          <span key={idx} className="paywall-value-point">
            {point}
          </span>
        ))}
      </div>

      {currentTier === 'free' ? (
        <PromoCodeInput
          applied={promo}
          onApplied={setPromo}
          onRemoved={() => setPromo(null)}
          disabled={loading}
        />
      ) : null}

      <div className="pricing-grid paywall-grid">
        {PAYWALL_PLANS.map((plan) => {
          const isOtherActivePlan = hasActivePaidPlan && plan.tier !== currentPlan;
          return (
            <PricingCard
              key={plan.tier}
              {...plan}
              currentTier={currentPlan}
              onSelect={handleSelectPlan}
              loading={loading}
              suggestFleet={plan.tier === 'fleet' && suggestFleet}
              ctaText={
                isOtherActivePlan ? 'Contact support to switch' : plan.ctaText
              }
              ctaTo={isOtherActivePlan ? '/support' : undefined}
            />
          );
        })}
      </div>
    </div>
  );
};

export default PricingPage;
